import { SEEN_HEALTH_SYSTEM } from '#src/codeSystems.ts'

// Phone Communication Extensions
const PHONE_COMMUNICATION_BASE = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/phone-communication-`
export const PHONE_COMMUNICATION = {
  CALLER_NUMBER: `${PHONE_COMMUNICATION_BASE}caller-number`,
  RECEIVER_NUMBER: `${PHONE_COMMUNICATION_BASE}receiver-number`,
  IS_OUTBOUND: `${PHONE_COMMUNICATION_BASE}is-outbound`,
  START_TIME: `${PHONE_COMMUNICATION_BASE}start-time`,
  END_TIME: `${PHONE_COMMUNICATION_BASE}end-time`,
  DURATION_SECONDS: `${PHONE_COMMUNICATION_BASE}duration-seconds`,
  IS_EXTERNAL: `${PHONE_COMMUNICATION_BASE}is-external`,
  ANSWER_TIME: `${PHONE_COMMUNICATION_BASE}answer-time`,
  CALLER_EXT_NUMBER: `${PHONE_COMMUNICATION_BASE}caller-ext-number`,
  CALLEE_EXT_NUMBER: `${PHONE_COMMUNICATION_BASE}callee-ext-number`,
  CALLER_EXT_TYPE: `${PHONE_COMMUNICATION_BASE}caller-ext-type`,
  CALLEE_EXT_TYPE: `${PHONE_COMMUNICATION_BASE}callee-ext-type`,
  CALL_RESULT: `${PHONE_COMMUNICATION_BASE}call-result`,
  IS_RECORDED: `${PHONE_COMMUNICATION_BASE}is-recorded`,
  MESSAGE_SUMMARY: `${PHONE_COMMUNICATION_BASE}message-summary`,
  EXTERNAL_RESOURCE_JSON: `${PHONE_COMMUNICATION_BASE}external-resource-json`,
} as const

// Appointment Extensions
export const APPOINTMENT_NOTES = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/appointment-notes`
// JSON-encoded record of a scheduler overriding flagged scheduling rules at
// booking time: { reason, ruleCodes } — the raw material for the
// rule-calibration feedback loop (a rule overridden constantly is
// miscalibrated).
export const APPOINTMENT_SCHEDULING_RULE_OVERRIDE = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/appointment-scheduling-rule-override`
// Link to the instances of a recurrence rules appointments with a unique identifier
export const APPOINTMENT_RECURRENCE_INSTANCE_LINK =
  'https://www.seenhealth.org/fhir/StructureDefinition/appointment-recurrence-instance-link'
/**
 * Average duration `valueQuantity` http://unitsofmeasure.org
 *
 * @example
 * {
 *   "url": `${SEEN_HEALTH_SYSTEM}/StructureDefinition/average-duration`,
 *   "valueQuantity": {
 *     "value": 30,
 *     "unit": "minutes",
 *     "system": "http://unitsofmeasure.org",
 *     "code": "min"
 *   }
 * }
 */
export const AVERAGE_DURATION = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/average-duration`

// Service Request Extensions
export const SERVICE_REQUEST_IDENTIFIER = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/service-request-identifier`

// Observation Extensions
// Marks the Observation.note annotation that holds the clinician's note, as
// against a note that carries the value (a vital's typed line).
export const OBSERVATION_CLINICIAN_NOTE = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/observation-clinician-note`
// How a computed observation was calculated: formula, rule id, version and
// input codes as sub-extensions.
export const OBSERVATION_CALCULATION_EXTENSION_URL = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/observation-calculation`

// Questionnaire Extensions
// On an answer-set concept: what choosing that answer scores.
export const ITEM_WEIGHT_EXTENSION_URL =
  'http://hl7.org/fhir/StructureDefinition/itemWeight'
export const ORDINAL_VALUE_EXTENSION_URL =
  'http://hl7.org/fhir/StructureDefinition/ordinalValue'
export const QUESTIONNAIRE_OPTION_PREFIX_EXTENSION_URL =
  'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix'
export const QUESTIONNAIRE_ITEM_CONTROL_EXTENSION_URL =
  'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl'
export const QUESTIONNAIRE_HIDDEN_EXTENSION_URL =
  'http://hl7.org/fhir/StructureDefinition/questionnaire-hidden'
export const QUESTIONNAIRE_RESPONSE_NAME_EXTENSION_URL =
  'http://hl7.org/fhir/StructureDefinition/questionnaire-response-name'
export const QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL =
  'http://hl7.org/fhir/StructureDefinition/questionnaire-item-display-link'
export const QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL =
  'http://hl7.org/fhir/StructureDefinition/questionnaire-display-accordion'
export const QUESTIONNAIRE_ITEM_TIME_RESULT_URL =
  'http://hl7.org/fhir/StructureDefinition/questionnaire-item-time-result'

export const QUESTIONNAIRE_ITEM_CONTROL_SYSTEM =
  'http://hl7.org/fhir/questionnaire-item-control'

// Value Constraints
export const MIN_VALUE = 'http://hl7.org/fhir/StructureDefinition/minValue'
export const MAX_VALUE = 'http://hl7.org/fhir/StructureDefinition/maxValue'

export const FOLDER_ID_SYSTEM =
  'http://hl7.org/fhir/us/core/StructureDefinition/folder-id'

export const DOCUMENT_REFERENCE_ATTACHMENT_MAGIC_LINK_EXTENSION = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/document-reference-attachment-magic-link`
export const DOCUMENT_REFERENCE_MAGIC_LINK_EXTENSION = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/document-reference-magic-link`

export const ATTACHMENT_CONVERSION_EXTENSION = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/attachment-conversion`

// Immunization Extensions
// Standard FHIR extensions
export const IMMUNIZATION_SERIES_EXTENSION =
  'http://hl7.org/fhir/StructureDefinition/immunization-series'
export const IMMUNIZATION_PROCEDURE_EXTENSION =
  'http://hl7.org/fhir/StructureDefinition/immunization-procedure'
export const IMMUNIZATION_REASON_EXTENSION =
  'http://hl7.org/fhir/StructureDefinition/immunization-reason'
export const IMMUNIZATION_SITE_EXTENSION =
  'http://hl7.org/fhir/StructureDefinition/immunization-site'
export const IMMUNIZATION_ROUTE_EXTENSION =
  'http://hl7.org/fhir/StructureDefinition/immunization-route'

// Custom Seen Health extensions for Elation-specific data
const IMMUNIZATION_BASE = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/immunization-`
export const IMMUNIZATION_VFC_ELIGIBILITY_EXTENSION = `${IMMUNIZATION_BASE}vfc-eligibility`
export const IMMUNIZATION_FUNDING_SOURCE_EXTENSION = `${IMMUNIZATION_BASE}funding-source`
export const IMMUNIZATION_INFORMATION_SOURCE_EXTENSION = `${IMMUNIZATION_BASE}information-source`
export const IMMUNIZATION_PUBLICITY_CODE_EXTENSION = `${IMMUNIZATION_BASE}publicity-code`
export const IMMUNIZATION_PATIENT_CONSENT_EXTENSION = `${IMMUNIZATION_BASE}patient-consent`
export const IMMUNIZATION_ALLOWED_SHARING_EXTENSION = `${IMMUNIZATION_BASE}allowed-sharing`
export const IMMUNIZATION_PRIORITY_POPULATION_EXTENSION = `${IMMUNIZATION_BASE}priority-population`
export const IMMUNIZATION_NDC_EXTENSION = `${IMMUNIZATION_BASE}ndc`
export const IMMUNIZATION_BARCODE_EXTENSION = `${IMMUNIZATION_BASE}barcode`
export const IMMUNIZATION_CAIR_SUBMISSION_EXTENSION = `${IMMUNIZATION_BASE}cair-submission`
export const IMMUNIZATION_DOSE_VALIDITY_EXTENSION = `${IMMUNIZATION_BASE}dose-validity`
export const IMMUNIZATION_DOSE_VALIDITY_REASON_EXTENSION = `${IMMUNIZATION_BASE}dose-validity-reason`
export const IMMUNIZATION_RAW_HL7_EXTENSION = `${IMMUNIZATION_BASE}raw-hl7`
export const IMMUNIZATION_ENTERED_IN_ERROR_REASON_EXTENSION = `${IMMUNIZATION_BASE}entered-in-error-reason`

export const METRIPORT_AI_SUMMARY_SYSTEM =
  'https://www.metriport.com/fhir/StructureDefinition/ai-summary'
export const METRIPORT_AI_SUMMARY_LAST_REFRESHED_SYSTEM =
  'https://www.metriport.com/fhir/StructureDefinition/ai-summary-last-refreshed'

export const DISENROLLMENT_REASON_SYSTEM = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/disenrollment-reason`
export const MEDICATION_DELIVERY_PREFERENCE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/medication-delivery-preference`
export const MEDICATION_DELIVERY_PREFERENCE_UPDATED_AT_SYSTEM = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/medication-delivery-preference-updated-at`

// Free-text natural-language scheduling preferences fed to the AI
// slot-suggestion agent. Soft guidance only, no hard constraints. The
// organization-level analog was retired: the org's scheduling rules document
// (ORGANIZATION_SCHEDULING_RULES_TEXT_SYSTEM below) is the single org source.
// Participant-level scheduling preferences are captured on the day-center
// schedule's note (Schedule.comment), not a dedicated participant extension.
export const PRACTITIONER_SCHEDULING_PREFERENCES_SYSTEM = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/practitioner-scheduling-preferences`

// Structured scheduling rules for the deterministic rules engine
// (src/scheduling-rules). The text extension holds the ops rules document
// verbatim; the config extension holds the compiled RulesConfig JSON. They
// are ALWAYS written together in one Organization update so every resource
// version carries a matched pair — history/revert restores both.
export const ORGANIZATION_SCHEDULING_RULES_TEXT_SYSTEM = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/organization-scheduling-rules-text`
export const ORGANIZATION_SCHEDULING_RULES_CONFIG_SYSTEM = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/organization-scheduling-rules-config`
